import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Sparkles } from 'lucide-react'

import { aiApi, extractTextOutput } from '@/api/ai'
import {
  createShot,
  patchShot,
  type ScriptScene,
  type Shot,
} from '@/api/studio-production'
import { ScriptAIModelField } from '@/features/studio/components/ScriptAIModelField'
import { formatShotSize, normalizeShotSize } from '@/features/studio/shot/storyboard-sheet'
import { ProtoBtn, ProtoDropdown, ProtoTextarea } from '@/features/studio/components/ui-kit'
import { pollAITask } from '@/features/studio/hooks/pollAITask'
import { useScriptTextModel } from '@/features/studio/hooks/useScriptTextModel'
import { buildStudioAIBillingPayload } from '@/features/studio/lib/studio-ai-billing'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'

export interface AIShotDraft {
  scene_no: number
  shot_size?: string
  camera_angle?: string
  camera_movement?: string
  description: string
  dialogue?: string
  sound_note?: string
  music_note?: string
  duration_sec?: number
  storyboard_note?: string
}

function formatSceneScript(sc: ScriptScene): string {
  const parts: string[] = []
  if (sc.heading?.trim()) parts.push(sc.heading.trim())
  if (sc.body?.trim()) parts.push(sc.body.trim())
  return parts.join('\n\n')
}

function parseAIShotDrafts(text: string, sceneNo: number): AIShotDraft[] {
  const trimmed = text.trim()
  const jsonMatch = trimmed.match(/\[[\s\S]*\]/)
  if (!jsonMatch) return []
  try {
    const parsed = JSON.parse(jsonMatch[0]) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((row): AIShotDraft | null => {
        const r = row as Record<string, unknown>
        const desc = String(r.description ?? r.desc ?? '').trim()
        if (!desc) return null
        const scene = Number(r.scene_no ?? r.scene ?? sceneNo)
        return {
          scene_no: scene > 0 ? scene : sceneNo,
          shot_size: r.shot_size ? normalizeShotSize(String(r.shot_size)) : undefined,
          camera_angle: r.camera_angle ? String(r.camera_angle) : undefined,
          camera_movement: r.camera_movement ? String(r.camera_movement) : undefined,
          description: desc,
          dialogue: r.dialogue ? String(r.dialogue) : undefined,
          sound_note: r.sound_note ? String(r.sound_note) : undefined,
          music_note: r.music_note ? String(r.music_note) : undefined,
          duration_sec: r.duration_sec != null ? Number(r.duration_sec) : undefined,
          storyboard_note: r.storyboard_note ? String(r.storyboard_note) : undefined,
        }
      })
      .filter((x): x is AIShotDraft => x != null)
  } catch {
    return []
  }
}

export function ShotEditorAIPanel({
  productionId,
  scriptId,
  episodeNo,
  scenes,
  selectedSceneNo,
  onSceneChange,
  onApplied,
}: {
  productionId: string
  scriptId: number
  episodeNo: number
  scenes: ScriptScene[]
  selectedSceneNo: number | null
  onSceneChange: (sceneNo: number) => void
  onApplied: (shots: Shot[]) => void
}) {
  const { t } = useTranslation()
  const scriptModel = useScriptTextModel()
  const { modelId, setModelId, textModels, selectedModel, loading: modelsLoading, ready: aiModelReady } = scriptModel
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<AIShotDraft[]>([])
  const [sceneScript, setSceneScript] = useState('')

  const activeSceneNo = selectedSceneNo ?? scenes[0]?.scene_no ?? null
  const activeScene = scenes.find((s) => s.scene_no === activeSceneNo)

  useEffect(() => {
    if (!activeScene) {
      setSceneScript('')
      return
    }
    setSceneScript(formatSceneScript(activeScene))
  }, [activeScene?.scene_no, activeScene?.heading, activeScene?.body])

  const sceneOptions = useMemo(
    () =>
      scenes.map((sc) => ({
        value: String(sc.scene_no),
        label: `S${String(sc.scene_no).padStart(2, '0')} · ${sc.heading?.trim() || t('shots.sceneUntitled')}`,
      })),
    [scenes, t],
  )

  async function handleGenerate() {
    if (!modelId || !sceneScript.trim() || activeSceneNo == null) return
    setBusy(true)
    setError(null)
    setDrafts([])
    try {
      const prompt = t('shots.aiScenePrompt', {
        episodeNo,
        sceneNo: activeSceneNo,
        body: sceneScript.trim().slice(0, 8000),
      })
      const task = await aiApi.submitText({
        model_id: modelId,
        prompt,
        // 与制片剧本 AI 面板一致：扣项目池，不按 shot_scene 派工额度预检
        payload: buildStudioAIBillingPayload(productionId),
      })
      const final = await pollAITask(task.id, () => {})
      if (final.status !== 'succeeded') {
        throw new Error(final.error_message || t('shots.aiFailed'))
      }
      const text = extractTextOutput(final.output).trim()
      const parsed = parseAIShotDrafts(text, activeSceneNo)
      if (parsed.length === 0) {
        throw new Error(t('shots.aiParseFailed'))
      }
      setDrafts(parsed)
    } catch (e) {
      setError(t(resolveApiErrorMessage(e, 'shots.aiFailed')))
    } finally {
      setBusy(false)
    }
  }

  async function handleApplyDrafts() {
    if (drafts.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const created: Shot[] = []
      for (const d of drafts) {
        let shot = await createShot(productionId, {
          script_id: scriptId,
          episode_no: episodeNo,
          scene_no: d.scene_no,
          description: d.description,
          duration_sec: d.duration_sec,
        })
        if (
          d.shot_size ||
          d.camera_angle ||
          d.camera_movement ||
          d.dialogue ||
          d.sound_note ||
          d.music_note ||
          d.storyboard_note
        ) {
          shot = await patchShot(productionId, shot.public_id, {
            shot_size: d.shot_size,
            camera_angle: d.camera_angle,
            camera_movement: d.camera_movement,
            dialogue: d.dialogue,
            sound_note: d.sound_note,
            music_note: d.music_note,
            storyboard_note: d.storyboard_note,
          })
        }
        created.push(shot)
      }
      setDrafts([])
      onApplied(created)
    } catch (e) {
      setError(t(resolveApiErrorMessage(e, 'shots.applyFailed')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-2.5 border-b border-white/[0.06] p-3">
        <p className="text-sm font-medium text-zinc-200">{t('shots.aiPanelTitle')}</p>
        <div>
          <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-zinc-600">
            {t('shots.aiSelectScene')}
          </label>
          {scenes.length === 0 ? (
            <p className="text-xs text-zinc-600">{t('shots.noScenes')}</p>
          ) : (
            <ProtoDropdown
              value={activeSceneNo != null ? String(activeSceneNo) : ''}
              onValueChange={(v) => onSceneChange(Number(v))}
              options={sceneOptions}
            />
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
        <label className="mb-1 block shrink-0 text-[10px] font-medium uppercase tracking-wide text-zinc-600">
          {t('shots.aiSceneScript')}
        </label>
        <ProtoTextarea
          className="studio-scrollbar-hidden min-h-0 flex-1 resize-none font-mono text-xs leading-relaxed"
          rows={12}
          value={sceneScript}
          onChange={(e) => setSceneScript(e.target.value)}
          placeholder={t('shots.aiSceneScriptPlaceholder')}
          disabled={scenes.length === 0}
        />

        {drafts.length > 0 ? (
          <div className="studio-scrollbar-hidden mt-3 max-h-40 shrink-0 overflow-y-auto border border-white/[0.06] bg-[#0e0e16] p-2">
            <p className="mb-1.5 text-[10px] text-zinc-500">{t('shots.aiDraftCount', { n: drafts.length })}</p>
            <ul className="space-y-1.5">
              {drafts.map((d, i) => (
                <li key={i} className="text-[11px] text-zinc-400">
                    <span className="font-mono text-indigo-300/90">
                      {[formatShotSize(d.shot_size ?? ''), d.camera_angle, d.camera_movement].filter(Boolean).join(' · ') || '—'}
                    </span>
                  <span className="ml-1">{d.description}</span>
                  {d.dialogue ? <p className="mt-0.5 text-zinc-500">「{d.dialogue}」</p> : null}
                </li>
              ))}
            </ul>
            <ProtoBtn className="mt-2 w-full" size="sm" disabled={busy} onClick={() => void handleApplyDrafts()}>
              {t('shots.aiApplyDrafts', { n: drafts.length })}
            </ProtoBtn>
          </div>
        ) : null}

        {error ? <p className="mt-2 shrink-0 text-xs text-rose-300">{error}</p> : null}
      </div>

      <div className="shrink-0 border-t border-white/[0.06] p-3">
        <ScriptAIModelField
          modelId={modelId}
          setModelId={setModelId}
          textModels={textModels}
          selectedModel={selectedModel}
          loading={modelsLoading}
          ready={aiModelReady}
          inline
          dropdownPortal
          trailing={
            <ProtoBtn
              size="sm"
              className="h-9 shrink-0 px-3"
              disabled={busy || !aiModelReady || !sceneScript.trim() || activeSceneNo == null}
              onClick={() => void handleGenerate()}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {t('shots.aiGenerateScene')}
            </ProtoBtn>
          }
        />
      </div>
    </div>
  )
}
